import sound from '../../common/sound/alert.mp3';

class SoundService {
  constructor() {
    this.audioElement = new Audio(sound);
    this.audioElement.preload = 'auto';
    this.audioElement.volume = 1.0;
    this.audioElement.playbackRate = 1.0;
    
    this.isEnabled = false;
    this.userInteracted = false;
    this.retryQueue = [];
    
    this.setupUserInteractionDetection();
  }

  // Detectar interação usuário para desbloquear autoplay
  setupUserInteractionDetection() {
    if (typeof window === 'undefined') return;

    const enableSound = () => {
      this.enableAudioContext();
    };

    // Eventos que indicam interação
    const events = ['click', 'pointerdown', 'keydown', 'touchstart', 'mousemove'];
    
    events.forEach(event => {
      window.addEventListener(event, enableSound, { once: true });
    });

    // Visibilidade da página
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        enableSound();
      }
    }, { once: true });
  }

  // Habilitar contexto de áudio
  enableAudioContext() {
    try {
      this.audioElement.muted = true;
      const playPromise = this.audioElement.play();
      
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.then(() => {
          this.audioElement.pause();
          this.audioElement.currentTime = 0;
          this.audioElement.muted = false;
          this.isEnabled = true;
          this.userInteracted = true;
          
          // Processar fila de retry
          this.processRetryQueue();
        }).catch(() => {
          this.audioElement.muted = false;
          this.isEnabled = true;
          this.userInteracted = true;
        });
      } else {
        this.audioElement.pause();
        this.audioElement.currentTime = 0;
        this.audioElement.muted = false;
        this.isEnabled = true;
        this.userInteracted = true;
      }
    } catch (error) {
      console.warn('Erro ao habilitar áudio:', error);
      this.audioElement.muted = false;
      this.isEnabled = true;
      this.userInteracted = true;
    }
  }

  // Tocar som
  async playNotificationSound() {
    if (!this.canPlaySound()) {
      this.scheduleRetry();
      return false;
    }

    try {
      // Resetar áudio
      this.audioElement.currentTime = 0;
      this.audioElement.muted = false;
      
      const playPromise = this.audioElement.play();
      
      if (playPromise && typeof playPromise.then === 'function') {
        await playPromise;
        return true;
      }
      
      return true;
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        this.scheduleRetry();
        this.isEnabled = false;
      }
      console.warn('Erro ao reproduzir som:', error);
      return false;
    }
  }

  // Verificar se pode tocar som
  canPlaySound() {
    return this.isEnabled && this.userInteracted;
  }

  // Agendar retry na próxima interação
  scheduleRetry() {
    if (this.retryQueue.length > 0) return; // Já tem retry agendado

    const onNextInteraction = () => {
      this.playNotificationSound().then(() => {
        this.retryQueue = [];
      });
      
      // Remove listeners
      ['click', 'keydown', 'touchstart'].forEach(event => {
        window.removeEventListener(event, onNextInteraction);
      });
    };

    ['click', 'keydown', 'touchstart'].forEach(event => {
      window.addEventListener(event, onNextInteraction, { once: true });
    });

    this.retryQueue.push(onNextInteraction);
  }

  // Processar fila de retry
  processRetryQueue() {
    if (this.retryQueue.length > 0) {
      this.playNotificationSound().then(() => {
        this.retryQueue = [];
      });
    }
  }

  // Configurar volume
  setVolume(volume) {
    this.audioElement.volume = Math.max(0, Math.min(1, volume));
  }

  // Status do serviço
  getStatus() {
    return {
      isEnabled: this.isEnabled,
      userInteracted: this.userInteracted,
      volume: this.audioElement.volume,
      retryQueueLength: this.retryQueue.length
    };
  }
}

// Singleton
const soundService = new SoundService();

// Debug global
if (typeof window !== 'undefined') {
  window.soundDebug = {
    getStatus: () => soundService.getStatus(),
    testSound: () => soundService.playNotificationSound(),
    enable: () => soundService.enableAudioContext()
  };
}

export default soundService;